import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, ExternalLink, Filter, Inbox, Link2, MinusCircle, XCircle } from 'lucide-react';
import clsx from 'clsx';
import type { DayMessage, MessageLink } from '@nlinks/shared';
import { useDayDetail } from '@/lib/queries';
import { formatNumber, truncateUrl } from '@/lib/format';
import { Alert, Badge, Card, CardHeader, EmptyState, Select, Spinner } from '@/components/ui';
import { InfoButton } from '@/components/InfoButton';

/** Reads the day back as a UTC date so the heading cannot drift by a timezone. */
function formatDayHeading(day: string): string {
  const parsed = new Date(`${day}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) return day;
  return parsed.toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function timeOf(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
}

/**
 * Outcome marker for one link.
 *
 * Unmatched mail is never opened, so its links carry no status at all — that
 * absence is shown as a dash rather than as a failure, which would read as
 * something having gone wrong.
 */
function LinkStatus({ status }: { status: MessageLink['status'] }) {
  if (!status) return <MinusCircle className="h-3.5 w-3.5 shrink-0 text-slate-600" aria-label="Not opened" />;
  if (status === 'SUCCESS')
    return <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-400" aria-label="Opened" />;
  if (status === 'FAILED') return <XCircle className="h-3.5 w-3.5 shrink-0 text-red-400" aria-label="Failed" />;
  if (status === 'SKIPPED')
    return <MinusCircle className="h-3.5 w-3.5 shrink-0 text-amber-400" aria-label="Skipped" />;
  return <Spinner className="h-3.5 w-3.5 shrink-0 text-slate-500" />;
}

/**
 * The links inside one message.
 *
 * Rendered inline beneath the row rather than as a floating panel: a hover card
 * holding a dozen URLs is unreadable and impossible to click into, and the
 * pointer has to cross it to reach anything. Hover reveals it, and focus or a
 * click keeps it open so it can actually be used.
 */
function LinkList({ links, matched }: { links: MessageLink[]; matched: boolean }) {
  if (links.length === 0) {
    return <p className="px-4 pb-3 text-xs text-slate-600">No links in this message.</p>;
  }

  return (
    <ul className="space-y-1.5 px-4 pb-3.5">
      {links.map((link, index) => (
        <li key={`${link.url}-${index}`} className="flex items-start gap-2 text-xs">
          <LinkStatus status={link.status} />
          <div className="min-w-0 flex-1">
            <a
              href={link.url}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-baseline gap-1 break-anywhere text-slate-400 transition hover:text-brand-300"
            >
              {truncateUrl(link.url, 92)}
              <ExternalLink className="h-3 w-3 shrink-0 self-center" aria-hidden />
            </a>
            {link.label ? <p className="truncate text-slate-600">{link.label}</p> : null}
            {link.skipReason ? <p className="text-amber-500/80">Skipped — {link.skipReason}</p> : null}
          </div>
        </li>
      ))}
      {!matched ? (
        <li className="pt-1 text-xs text-slate-600">
          These links were not opened — this message matched none of your keywords.
        </li>
      ) : null}
    </ul>
  );
}

function MessageRow({ message }: { message: DayMessage }) {
  const [pinned, setPinned] = useState(false);
  const [hovered, setHovered] = useState(false);
  const open = hovered || pinned;

  return (
    <li
      className={clsx(
        'border-l-2 transition-colors',
        // The single identifying signal: matched mail is tinted and bordered in
        // the brand colour used for "matched" everywhere else in the app.
        message.matched ? 'border-l-brand-400 bg-brand-500/[0.04]' : 'border-l-transparent',
        open && 'bg-surface-raised/60',
      )}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        type="button"
        onClick={() => setPinned((v) => !v)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        aria-expanded={open}
        className="flex w-full items-start gap-3 px-4 py-3 text-left"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className={clsx('truncate text-sm font-medium', message.matched ? 'text-slate-100' : 'text-slate-400')}>
              {message.subject}
            </p>
            {message.matched ? (
              <Badge tone="brand">Matched</Badge>
            ) : (
              <Badge tone="neutral">No match</Badge>
            )}
          </div>

          <p className="mt-0.5 truncate text-xs text-slate-500">
            {message.fromName ? `${message.fromName} · ` : ''}
            {message.fromAddress}
          </p>

          {message.matchedKeywords.length > 0 ? (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {message.matchedKeywords.map((keyword) => (
                <span
                  key={keyword}
                  className="rounded bg-brand-500/10 px-1.5 py-0.5 text-[11px] font-medium text-brand-300"
                >
                  {keyword}
                </span>
              ))}
            </div>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center gap-3 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1 tabular-nums">
            <Link2 className="h-3.5 w-3.5" aria-hidden />
            {message.links.length}
          </span>
          <span className="w-24 truncate text-right">{message.mailboxLabel}</span>
          <span className="w-12 text-right tabular-nums">{timeOf(message.receivedAt)}</span>
        </div>
      </button>

      {open ? <LinkList links={message.links} matched={message.matched} /> : null}
    </li>
  );
}

type SortOrder = 'newest' | 'oldest' | 'mailbox';

export function DayDetailPage() {
  const { day } = useParams<{ day: string }>();
  const { data, isLoading, isError } = useDayDetail(day);

  const [mailbox, setMailbox] = useState('');
  const [sort, setSort] = useState<SortOrder>('newest');

  const totals = data?.totals;

  /**
   * Mailboxes present in this day's mail, not every mailbox on the account.
   * Offering a filter that returns nothing is worse than not offering it.
   */
  const mailboxes = useMemo(
    () => [...new Set((data?.messages ?? []).map((m) => m.mailboxLabel))].sort((a, b) => a.localeCompare(b)),
    [data?.messages],
  );

  const visible = useMemo(() => {
    const rows = (data?.messages ?? []).filter((m) => !mailbox || m.mailboxLabel === mailbox);
    // Copied before sorting — the query cache hands back the same array on every
    // render, and sorting in place would mutate it.
    return [...rows].sort((a, b) => {
      if (sort === 'mailbox') {
        return a.mailboxLabel.localeCompare(b.mailboxLabel) || b.receivedAt.localeCompare(a.receivedAt);
      }
      return sort === 'oldest'
        ? a.receivedAt.localeCompare(b.receivedAt)
        : b.receivedAt.localeCompare(a.receivedAt);
    });
  }, [data?.messages, mailbox, sort]);

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs text-slate-500 transition hover:text-slate-300"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
          Back to dashboard
        </Link>
        <h1 className="mt-2 text-xl font-semibold tracking-tight text-slate-100">
          {day ? formatDayHeading(day) : 'Day'}
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Everything that landed in your mailboxes this day. Times are UTC, matching the dashboard.
        </p>
      </div>

      {isError ? <Alert severity="error">That day could not be loaded.</Alert> : null}

      {totals ? (
        <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-6">
          {[
            { label: 'Arrived', value: totals.arrived, tone: 'text-slate-100' },
            { label: 'Matched', value: totals.matched, tone: 'text-brand-300' },
            { label: 'No match', value: totals.unmatched, tone: 'text-slate-400' },
            { label: 'Links found', value: totals.linksFound, tone: 'text-slate-100' },
            { label: 'Mails opened', value: totals.mailsOpened, tone: 'text-emerald-300' },
            { label: 'Not opened', value: totals.mailsNotOpened, tone: 'text-red-300' },
          ].map((item) => (
            <Card key={item.label} className="p-4">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{item.label}</p>
              <p className={clsx('mt-1 text-xl font-semibold tabular-nums', item.tone)}>
                {formatNumber(item.value)}
              </p>
            </Card>
          ))}
        </div>
      ) : null}

      {data && data.clearedCount > 0 ? (
        <Alert severity="info">
          {formatNumber(data.clearedCount)} message(s) that arrived this day are no longer listed. Detail of mail
          that matched nothing is cleared whenever your keywords change — the arrival count above still includes
          them.
        </Alert>
      ) : null}

      <Card>
        <CardHeader
          title="Messages"
          description={
            <span className="inline-flex items-center gap-1.5">
              Hover a message to see its links
              <InfoButton label="Message links">
                Matched mail shows each link with how the visit went. Mail that matched nothing is listed with its
                links too, but none of them were opened.
              </InfoButton>
            </span>
          }
          action={
            <div className="flex flex-wrap items-center gap-2">
              {mailboxes.length > 1 ? (
                <Select
                  value={mailbox}
                  onChange={(e) => setMailbox(e.target.value)}
                  className="w-auto min-w-[11rem] text-xs"
                  aria-label="Filter by mailbox"
                >
                  <option value="">All mailboxes</option>
                  {mailboxes.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </Select>
              ) : null}
              <Select
                value={sort}
                onChange={(e) => setSort(e.target.value as SortOrder)}
                className="w-auto text-xs"
                aria-label="Sort messages"
              >
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="mailbox">Group by mailbox</option>
              </Select>
            </div>
          }
        />

        {isLoading ? (
          <div className="p-4">
            <div className="skeleton h-48 w-full" />
          </div>
        ) : visible.length > 0 ? (
          <>
            {mailbox ? (
              <p className="border-b border-surface-border px-4 py-2 text-xs text-slate-500">
                Showing {formatNumber(visible.length)} of {formatNumber(data?.messages.length ?? 0)} —{' '}
                <button
                  type="button"
                  onClick={() => setMailbox('')}
                  className="text-brand-300 transition hover:text-brand-200"
                >
                  clear filter
                </button>
              </p>
            ) : null}
            <ul className="divide-y divide-surface-border">
              {visible.map((message) => (
                <MessageRow key={message.id} message={message} />
              ))}
            </ul>
          </>
        ) : data && data.messages.length > 0 ? (
          <EmptyState
            icon={<Inbox className="h-6 w-6" />}
            title="No mail from that mailbox"
            description="Nothing arrived in the selected mailbox on this day. Choose “All mailboxes” to see the rest."
          />
        ) : (
          <EmptyState
            icon={<Inbox className="h-6 w-6" />}
            title="Nothing on record for this day"
            description="No mail is stored for this date. It may predate your mailbox start date, or its detail may have been cleared by a keyword change."
          />
        )}
      </Card>

      {data && data.messages.length > 0 && totals && totals.matched === 0 ? (
        <Alert severity="warning" title="Nothing matched this day">
          <span className="inline-flex items-center gap-1.5">
            <Filter className="h-3.5 w-3.5" aria-hidden />
            Every message above was filtered out, so no links were opened. Widening your keywords would catch more.
          </span>
        </Alert>
      ) : null}
    </div>
  );
}
